import api from '../../../services/api'
import type { BruktbilFilters } from '../useBruktbilFilters'

export interface BelopBucket {
  label: string
  min_belop: number
  max_belop: number | null
  count: number
  avg_belop: number | null
}

export interface BelopFordelingResponse {
  buckets: BelopBucket[]
  total: number
  v_financed_available: boolean
}

export interface MerkeModellPrisRow {
  merke: string | null
  modell: string | null
  count: number
  avg_belop: number | null
}

export interface PerMerkeModellResponse {
  rows: MerkeModellPrisRow[]
  total: number
  v_financed_available: boolean
}

export type MinePostnummerResult =
  | { kind: 'ok'; data: BelopFordelingResponse }
  | { kind: 'no_postnummer' }

/** No kjennemerke or postnummer (#912): beside the loan amount they identify the buyer. */
export interface ObservasjonRow {
  svv_id: number
  klasse: string | null
  merke: string | null
  modell: string | null
  aarsmodell: number | null
  belop: number
  dato_overforing: string | null
  forstegang_reg_norge_dato: string | null
  seller_kommunenavn: string | null
  seller_fylke: string | null
  buyer_kommunenavn: string | null
  buyer_fylke: string | null
}

export interface ObservasjonerResponse {
  rows: ObservasjonRow[]
  total: number
  page: number
  page_size: number
}

export interface ObservasjonerParams extends Pick<BruktbilFilters, 'from' | 'to' | 'klasse' | 'varebil' | 'omregistrering'> {
  selgerPostnummer?: string | null
  kjoperPostnummer?: string | null
  merke?: string[]
  modell?: string[]
  aarsmodell_from?: number
  aarsmodell_to?: number
  page?: number
  page_size?: number
  sort?: string
  sort_dir?: 'asc' | 'desc'
}

type BaseParams = Pick<BruktbilFilters, 'from' | 'to' | 'klasse' | 'varebil' | 'omregistrering'> & { selgerPostnummer?: string | null; kjoperPostnummer?: string | null }

function baseParams(f: BaseParams) {
  return {
    from_date: f.from, to_date: f.to, klasse: f.klasse, varebil: f.varebil, omregistrering: f.omregistrering || undefined,
    ...(f.selgerPostnummer ? { selger_postnummer: f.selgerPostnummer.split(',') } : {}),
    ...(f.kjoperPostnummer ? { kjoper_postnummer: f.kjoperPostnummer.split(',') } : {}),
  }
}

export function fetchBelopFordeling(filters: BaseParams): Promise<{ data: BelopFordelingResponse }> {
  return api.get('/bruktbil/priser/belop-fordeling', {
    params: baseParams(filters),
    paramsSerializer: { indexes: null },
  })
}

export function fetchPerMerkeModell(
  filters: BaseParams,
  merkeFilter: string[],
  modellFilter: string[],
  limit = 50,
  offset = 0,
): Promise<{ data: PerMerkeModellResponse }> {
  return api.get('/bruktbil/priser/per-merke-modell', {
    params: {
      ...baseParams(filters),
      'merke[]': merkeFilter,
      'modell[]': modellFilter,
      limit,
      offset,
    },
    paramsSerializer: { indexes: null },
  })
}

export async function fetchMinePostnummer(filters: BaseParams): Promise<MinePostnummerResult> {
  try {
    const r = await api.get<BelopFordelingResponse>('/bruktbil/priser/mine-postnummer', {
      params: baseParams(filters),
      paramsSerializer: { indexes: null },
    })
    return { kind: 'ok', data: r.data }
  } catch (err: any) {
    if (err?.response?.status === 400) {
      return { kind: 'no_postnummer' }
    }
    throw err
  }
}

export interface MerkeOption { merke: string | null; count: number }
export interface ModellOption { modell: string | null; count: number }

// Build the merke-modell URLs with URLSearchParams to guarantee that the
// merke[] alias on the FastAPI side actually receives the values. The axios
// `paramsSerializer: { indexes: null }` path occasionally drops or
// double-encodes the brackets in the key, which silently returns ALL modeller
// when the backend reads filters.merke as [].
function buildMerkeModellQS(filters: BaseParams, extras: Record<string, string | number | string[]>): string {
  const qs = new URLSearchParams()
  qs.set('from_date', filters.from)
  qs.set('to_date', filters.to)
  for (const k of filters.klasse) qs.append('klasse', k)
  // Omit varebil for "alle" (undefined); false = personbil, true = varebil.
  if (filters.varebil !== undefined) qs.set('varebil', String(filters.varebil))
  // Omit omregistrering when false/undefined — only send true when the filter is active.
  if (filters.omregistrering) qs.set('omregistrering', 'true')
  if (filters.selgerPostnummer) for (const p of filters.selgerPostnummer.split(',')) qs.append('selger_postnummer', p.trim())
  if (filters.kjoperPostnummer) for (const p of filters.kjoperPostnummer.split(',')) qs.append('kjoper_postnummer', p.trim())
  for (const [key, val] of Object.entries(extras)) {
    if (Array.isArray(val)) {
      for (const v of val) qs.append(key, v)
    } else {
      qs.set(key, String(val))
    }
  }
  return qs.toString()
}

// financed_only=true so the Priser-page dropdowns only list merker/modeller
// that actually have at least one observation with a loan amount > 0 — without
// this the dropdown is dominated by brands that yield 0 rows in the table.
export function fetchMerkeOptions(
  filters: BaseParams,
  limit = 250,
): Promise<{ data: { rows: MerkeOption[] } }> {
  const qs = buildMerkeModellQS(filters, { limit, financed_only: 'true' })
  return api.get(`/bruktbil/merke-modell/merke?${qs}`)
}

export function fetchModellOptions(
  filters: BaseParams,
  merke: string[],
  limit = 250,
): Promise<{ data: { rows: ModellOption[] } }> {
  // FastAPI declares the param as `merke: List[str] = Query(alias="merke[]")`,
  // so the literal key in the URL must be `merke[]`, not `merke`.
  const qs = buildMerkeModellQS(filters, { 'merke[]': merke, limit, financed_only: 'true' })
  return api.get(`/bruktbil/merke-modell/modell?${qs}`)
}

export function fetchObservasjoner(p: ObservasjonerParams): Promise<{ data: ObservasjonerResponse }> {
  // Build the URL explicitly. axios' paramsSerializer was silently dropping
  // the merke/modell array params here too, which made the filter look
  // broken (results never narrowed). FastAPI expects `merke=Toyota&merke=Yaris`
  // for List[str] params with no alias — same as klasse.
  const qs = new URLSearchParams()
  qs.set('from_date', p.from)
  qs.set('to_date', p.to)
  for (const k of p.klasse) qs.append('klasse', k)
  if (p.varebil !== undefined) qs.set('varebil', String(p.varebil))
  if (p.omregistrering) qs.set('omregistrering', 'true')
  if (p.selgerPostnummer) for (const pn of p.selgerPostnummer.split(',')) qs.append('selger_postnummer', pn.trim())
  if (p.kjoperPostnummer) for (const pn of p.kjoperPostnummer.split(',')) qs.append('kjoper_postnummer', pn.trim())
  if (p.merke) for (const m of p.merke) qs.append('merke', m)
  if (p.modell) for (const m of p.modell) qs.append('modell', m)
  if (p.aarsmodell_from !== undefined) qs.set('aarsmodell_from', String(p.aarsmodell_from))
  if (p.aarsmodell_to !== undefined) qs.set('aarsmodell_to', String(p.aarsmodell_to))
  qs.set('page', String(p.page ?? 1))
  qs.set('page_size', String(p.page_size ?? 50))
  qs.set('sort', p.sort ?? 'dato_overforing')
  qs.set('sort_dir', p.sort_dir ?? 'desc')
  return api.get(`/bruktbil/priser/observasjoner?${qs.toString()}`)
}
